import { maxQuantityFor } from "@/components/pricing/clinic-seat-pricing";
import type { PlanPriceResult } from "@/lib/pricing/calculate-plan-price";

// Legacy source: src/components/billing/checkout/checkout-types.ts (1:1 port).
// The pricing types below were imported from "@/components/pricing/pricing-section"
// in legacy; they are defined here so SPA checkout files import them from
// "./checkout-types" instead.

// ---------------------------------------------------------------------------
// Pricing types (legacy: src/components/pricing/pricing-section.tsx)
// ---------------------------------------------------------------------------

export interface PricingFeature {
  label: string;
  status: "active" | "new" | "soon";
}

export interface VolumeTierInfo {
  minProviders: number;
  maxProviders: number;
  pricePerProvider: number;
  discountPct: number;
}

export interface MessagingLimits {
  sms: number; // -1 = unlimited, 0 = none
  push: number;
  viber: number;
  whatsapp: number;
}

export interface PricingTier {
  name: string;
  planCode?: string;
  priceMonthly: number; // 0 = free
  priceAnnual: number; // 0 = free, annual total (10 months)
  desc: string;
  features: string[];
  featuresWithStatus?: PricingFeature[];
  extra?: string; // "+€39 / extra 50 programari"
  cta: string;
  highlight?: boolean;
  badge?: string;
  subtitle?: string;
  perProvider?: string;
  fee?: string;
  savings?: string;
  // New fields
  messaging?: MessagingLimits;
  volumeTiers?: VolumeTierInfo[];
  extraProviderPrice?: number; // base price per extra provider (before volume discount)
  bookingsPerMonth?: number; // -1 = unlimited, 0 = none
  aiNotesPerMonth?: number; // -1 = unlimited, 0 = none
  providersIncluded?: number;
  byopAllowed?: boolean;
  extraSmsPrice?: number; // price per extra SMS
  extraBookingsPrice?: number; // price per extra bookings batch
  extraBookingsAmount?: number; // how many bookings per batch
  // Release gating
  visibility?: "active" | "coming_soon"; // 'draft' is filtered out before it reaches this component
  interestedCount?: number; // social proof for coming_soon plans
}

export interface FoundingProviderInfo {
  enabled: boolean;
  slots: number;
  taken: number;
  slotsRemaining: number;
  lifetimePlanLabel: string;
  lifetimePlanPrice: number; // monthly price in EUR
  lifetimePlanAnnual: number | null; // annual price in EUR (with discount)
  lifetimeMonths: number; // 0 = unlimited / lifetime
  badge: boolean;
  // Founding grants ONE plan since 2026-09-20. The API still answers the bonus
  // keys as null/0 for one release; nothing reads them any more.
}

/**
 * Pack add-on option returned by GET /api/billing/plan-addons. Each entry
 * is a feature with `batchSize > 0 && batchPrice > 0` on the current plan —
 * e.g. "+50 bookings for €39" or "+100 SMS for €15".
 *
 * Mirror of `PlanAddonOption` on the server; duplicated here to avoid a
 * client → server import path cycle.
 */
export interface PlanAddonOption {
  featureKey: string;
  labelRo: string;
  labelEn: string;
  category: string;
  batchSize: number;
  batchPriceEur: number;
  currency: string;
  basePlanLimit: number | null;
}

// ---------------------------------------------------------------------------
// Step definitions
// ---------------------------------------------------------------------------

export type CheckoutStep = "plan" | "configure" | "billing" | "card" | "pay" | "success" | "error";

export const STEP_META: Record<
  Exclude<CheckoutStep, "success" | "error">,
  { labelKey: string; icon: string }
> = {
  plan: { labelKey: "stepPlan", icon: "LayoutGrid" },
  configure: { labelKey: "stepConfigure", icon: "SlidersHorizontal" },
  billing: { labelKey: "stepBilling", icon: "Receipt" },
  card: { labelKey: "stepCard", icon: "Wallet" },
  pay: { labelKey: "stepPay", icon: "CreditCard" },
};

// ---------------------------------------------------------------------------
// Saved entities
// ---------------------------------------------------------------------------

export interface SavedCard {
  id: string;
  brand: string;
  last4: string;
  expMonth?: number;
  expYear?: number;
  isDefault: boolean;
}

export interface BillingProfileOption {
  id: string;
  profileName: string;
  businessName?: string;
  vatNumber?: string;
  registrationNumber?: string;
  fiscalType: string;
  country: string;
  address: string;
  isDefault: boolean;
  /** What VIES said of `vatNumber` — a "valid" EU company outside BG pays net (lib/pricing/buyer-price.ts). */
  vatStatus?: "valid" | "invalid" | "unverified" | null;
}

export interface ExistingSubscription {
  id: string;
  planTier: string;
  planInterval: string;
  price: number;
  currency: string;
  currentPeriodEnd: string | null;
  entityType?: string;
  quantity?: number;
  plan: {
    code: string;
    name: Record<string, string>;
    features: Record<string, boolean>;
  } | null;
}

// ---------------------------------------------------------------------------
// Modal props
// ---------------------------------------------------------------------------

export interface CheckoutModalProps {
  open: boolean;
  onClose: () => void;
  /** Called when the flow ends through its own buttons (success step, plan
   *  change) — not when the success step is closed with the header X. */
  onSuccess?: () => void;
  /** Called once the purchase is paid, however the modal is closed afterwards. */
  onPaymentComplete?: () => void;
  /**
   * The purchase was refused because the workspace's services are paused
   * (409 SERVICES_PAUSED): the caller closes the modal and shows its pause
   * card. Without it the refusal only explains where to restart them.
   */
  onShowServicePause?: () => void;
  /** Pre-selected plan — skip plan step */
  planCode?: string;
  planData?: PricingTier;
  entityType: "provider" | "facility" | "business" | "user";
  entityId?: string;
  /** Set by workspace billing routes; used when the plan being bought is for
   *  the same kind of entity as the workspace. */
  workspace?: BillingWorkspace;
  initialInterval?: "monthly" | "annual";
  initialQuantity?: number;
  /** Existing subscription for change-plan mode */
  subscription?: ExistingSubscription | null;
  /** Default category for plan step */
  defaultCategory?: string;
}

// ---------------------------------------------------------------------------
/** The workspace the checkout was opened from. Which entity a purchase is FOR
 *  is resolved on the server from the caller's own links
 *  (apps/api/src/lib/billing/resolve-entity.ts); a caller who manages several
 *  businesses — or several facilities — must say which one, or checkout
 *  answers 400 `business_required`. The route knows: the id is in its URL. */
export interface BillingWorkspace {
  entityType: "provider" | "facility" | "business" | "user";
  /** Undefined for provider/user — the server derives those from the caller. */
  entityId?: string;
}

// Checkout state (useReducer)
// ---------------------------------------------------------------------------

export interface CheckoutState {
  // Plan selection
  selectedPlanCode: string;
  selectedPlanData: PricingTier | undefined;
  selectedPlanName: string;
  entityType: "provider" | "facility" | "business" | "user";

  // Configuration
  interval: "monthly" | "annual";
  quantity: number;
  /**
   * Pack add-ons selected at checkout. `packs[featureKey] = count` — each
   * unit adds a full batch (e.g. +50 bookings) at the plan's configured
   * batch price. Consumed by `calculatePlanPrice` + sent to Stripe as
   * separate line items by /api/checkout/create.
   */
  addons: { packs: Record<string, number> };
  /**
   * Options loaded from GET /api/billing/plan-addons once a plan is picked.
   * Empty array when the plan has no batch packs configured or the fetch
   * hasn't returned yet — UI renders nothing in that case.
   */
  availablePacks: PlanAddonOption[];

  // Billing
  billingProfiles: BillingProfileOption[];
  selectedProfileId: string | null;
  billingChoice: "individual" | "business";
  billingSelectedBusinessId: string | null;
  billingShowNewBusiness: boolean;
  billingForm: {
    businessName: string;
    vatNumber: string;
    registrationNumber: string;
    country: string;
    address: string;
  };

  // Cards
  savedCards: SavedCard[];
  selectedCardId: string | null; // null = new card
  authorizeRecurring: boolean;

  // Payment
  clientSecret: string | null;

  // Wallet
  walletBalance: number;

  // Post-payment
  newSubscriptionId: string | null;
  seatAutoAllocated: boolean;

  // Change mode
  changeResult: {
    success: boolean;
    type: string;
    effectiveDate: string;
    proratedAmount?: number;
  } | null;

  // UI
  loading: boolean;
  cardPaymentLoading: boolean;
  threeDSecureLoading: boolean;
  error: string | null;
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export type CheckoutAction =
  | {
      type: "SELECT_PLAN";
      planCode: string;
      planData: PricingTier;
      planName: string;
      entityType: "provider" | "facility" | "business" | "user";
    }
  | { type: "SET_INTERVAL"; interval: "monthly" | "annual" }
  | { type: "SET_QUANTITY"; quantity: number }
  | { type: "SET_ADDON_PACK"; featureKey: string; count: number }
  | { type: "SET_AVAILABLE_PACKS"; packs: PlanAddonOption[] }
  | { type: "SET_BILLING_PROFILES"; profiles: BillingProfileOption[] }
  | { type: "SET_SELECTED_PROFILE"; profileId: string | null }
  | { type: "SET_BILLING_CHOICE"; choice: "individual" | "business" }
  | { type: "SET_BILLING_COMPANY_ID"; businessId: string | null }
  | { type: "SET_BILLING_SHOW_NEW"; show: boolean }
  | { type: "SET_BILLING_FORM"; form: Partial<CheckoutState["billingForm"]> }
  | { type: "SET_SAVED_CARDS"; cards: SavedCard[] }
  | { type: "SET_SELECTED_CARD"; cardId: string | null }
  | { type: "SET_CLIENT_SECRET"; clientSecret: string }
  | { type: "SET_WALLET_BALANCE"; balance: number }
  | { type: "PAYMENT_SUCCESS"; subscriptionId: string | null }
  | { type: "SEAT_ALLOCATED" }
  | {
      type: "CHANGE_RESULT";
      result: { success: boolean; type: string; effectiveDate: string; proratedAmount?: number };
    }
  | { type: "SET_LOADING"; loading: boolean }
  | { type: "SET_CARD_LOADING"; loading: boolean }
  | { type: "SET_3DS_LOADING"; loading: boolean }
  | { type: "SET_ERROR"; error: string | null }
  | { type: "RESET"; initial: Partial<CheckoutState> };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function createInitialState(props: CheckoutModalProps): CheckoutState {
  return {
    selectedPlanCode: props.planCode || "",
    selectedPlanData: props.planData,
    selectedPlanName: props.planData?.name || props.planCode || "",
    entityType: props.entityType,
    interval: props.initialInterval || "monthly",
    quantity: props.initialQuantity || 1,
    addons: { packs: {} },
    availablePacks: [],
    billingProfiles: [],
    selectedProfileId: null,
    billingChoice: "individual",
    billingSelectedBusinessId: null,
    billingShowNewBusiness: false,
    billingForm: {
      businessName: "",
      vatNumber: "",
      registrationNumber: "",
      country: "RO",
      address: "",
    },
    savedCards: [],
    selectedCardId: null,
    authorizeRecurring: true,
    clientSecret: null,
    walletBalance: 0,
    newSubscriptionId: null,
    seatAutoAllocated: false,
    changeResult: null,
    loading: false,
    cardPaymentLoading: false,
    threeDSecureLoading: false,
    error: null,
  };
}

/**
 * An embedded Stripe session is created for one exact purchase (line items,
 * interval, invoice profile in its metadata). Going back and changing any of
 * them must drop it, or the pay step re-mounts the old session and Stripe
 * charges — and the invoice names — what was chosen before.
 */
const STALE_SESSION = { clientSecret: null } as const;

export function checkoutReducer(state: CheckoutState, action: CheckoutAction): CheckoutState {
  switch (action.type) {
    case "SELECT_PLAN":
      // Reset pack selection + available packs on plan change — the next plan
      // likely has different add-on options, and keeping stale counts around
      // could charge for packs that don't exist on the new plan.
      return {
        ...state,
        selectedPlanCode: action.planCode,
        selectedPlanData: action.planData,
        selectedPlanName: action.planName,
        entityType: action.entityType,
        addons: { packs: {} },
        availablePacks: [],
        ...STALE_SESSION,
      };
    case "SET_INTERVAL":
      return { ...state, interval: action.interval, ...STALE_SESSION };
    case "SET_QUANTITY": {
      // Same cap as the API (`maxQuantityFor`): a clinic plan goes to 500
      // doctors; the flat 100 snapped a 150-doctor clinic down on the first tap.
      const max = state.selectedPlanData ? maxQuantityFor(state.selectedPlanData) : 100;
      const quantity = Math.max(1, Math.min(max, action.quantity));
      return { ...state, quantity, ...(quantity !== state.quantity ? STALE_SESSION : {}) };
    }
    case "SET_ADDON_PACK": {
      const next = { ...state.addons.packs };
      const clamped = Math.max(0, Math.min(20, Math.floor(action.count)));
      if (clamped === 0) delete next[action.featureKey];
      else next[action.featureKey] = clamped;
      return { ...state, addons: { packs: next }, ...STALE_SESSION };
    }
    case "SET_AVAILABLE_PACKS":
      return { ...state, availablePacks: action.packs };
    case "SET_BILLING_PROFILES":
      return { ...state, billingProfiles: action.profiles };
    case "SET_BILLING_CHOICE":
      return { ...state, billingChoice: action.choice };
    case "SET_BILLING_COMPANY_ID":
      return { ...state, billingSelectedBusinessId: action.businessId };
    case "SET_BILLING_SHOW_NEW":
      return { ...state, billingShowNewBusiness: action.show };
    case "SET_BILLING_FORM":
      return { ...state, billingForm: { ...state.billingForm, ...action.form } };
    case "SET_SELECTED_PROFILE":
      return {
        ...state,
        selectedProfileId: action.profileId,
        ...(action.profileId !== state.selectedProfileId ? STALE_SESSION : {}),
      };
    case "SET_SAVED_CARDS":
      return { ...state, savedCards: action.cards };
    case "SET_SELECTED_CARD":
      return { ...state, selectedCardId: action.cardId };
    case "SET_CLIENT_SECRET":
      return { ...state, clientSecret: action.clientSecret };
    case "SET_WALLET_BALANCE":
      return { ...state, walletBalance: action.balance };
    case "PAYMENT_SUCCESS":
      return { ...state, newSubscriptionId: action.subscriptionId };
    case "SEAT_ALLOCATED":
      return { ...state, seatAutoAllocated: true };
    case "CHANGE_RESULT":
      return { ...state, changeResult: action.result };
    case "SET_LOADING":
      return { ...state, loading: action.loading };
    case "SET_CARD_LOADING":
      return { ...state, cardPaymentLoading: action.loading };
    case "SET_3DS_LOADING":
      return { ...state, threeDSecureLoading: action.loading };
    case "SET_ERROR":
      return { ...state, error: action.error };
    case "RESET":
      return { ...createInitialState({} as CheckoutModalProps), ...action.initial };
    default:
      return state;
  }
}

/** Compute which steps are visible */
export function getVisibleSteps(props: CheckoutModalProps): CheckoutStep[] {
  // Change-plan mode: only configure step
  if (props.subscription) return ["configure"];

  // Pre-set plan: skip plan selection
  if (props.planCode) return ["configure", "billing", "card", "pay"];

  // Full flow
  return ["plan", "configure", "billing", "card", "pay"];
}

/** Compute price from state */
export { type PlanPriceResult };
